import { map } from 'rxjs/operators';
import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Observable, of, Subscription, switchMap } from 'rxjs';
import { UsersProvider } from '../../../users/providers/users-provider';
import {
  MAT_DIALOG_DATA,
  MatDialogActions, MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { User } from "../../../common/models/user";
import { Organization } from "../../../common/models/organization";
import { ConfigProvider } from "../../../config-provider.service";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { LangsProvider } from "../../../common/providers/langs-provider";
import { OrganizationsProvider, UserOrganizationRow } from "../../providers/organizations-provider";
import { OrganizationMembership } from "../../../common/models/organization-membership";
import { MatError, MatFormField } from "@angular/material/form-field";
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from "@angular/material/autocomplete";
import { MatIcon } from "@angular/material/icon";
import { AsyncPipe, NgClass, NgForOf, NgIf } from "@angular/common";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatInput } from "@angular/material/input";
import { MatButton } from "@angular/material/button";

@Component({
  selector: 'organizations-new-user-dialog',
  providers: [],
  styleUrls: ['./new-user-dialog.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    MatDialogTitle,
    MatDialogContent,
    MatFormField,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatIcon,
    MatAutocomplete,
    MatOption,
    AsyncPipe,
    NgClass,
    MatDialogActions,
    MatProgressBar,
    MatInput,
    MatButton,
    MatDialogClose,
    MatError,
    NgIf,
    NgForOf
  ],
  templateUrl: './new-user-dialog.html'
})
export class OrganizationsNewUserDialogComponent implements AfterViewInit, OnDestroy {
    public isLoading = true;
    public firstNameFormControl: FormControl = new FormControl('', [
        control => this.checkName(control.value),
    ]);
    public lastNameFormControl: FormControl = new FormControl('', [
        control => this.checkName(control.value),
    ]);
    public isAdminError = false;
    public alreadyMemberError = false;
    public emailUnavailableError = false;
    public emailFormControl: FormControl = new FormControl('', [
        // doc : https://blog.thoughtram.io/angular/2016/03/14/custom-validators-in-angular-2.html#building-a-custom-validator
        control => this.checkEmail(control.value),
        control => this.isAdminError ? { isAdmin: { valid: false } } : null,
        control => this.alreadyMemberError ? { alreadyMember: { valid: false } } : null,
        control => this.emailUnavailableError ? { unavailable: { valid: false } } : null
    ]);
    public isSearchMode = true;
    public searchPlaceholder = '';
    public selectedUser: User| null = null;

    public isSearchEnabled = true;
    public availableUsers: User[] = [];
    public avoidedUsers: User[] = [];
    public addAsAdmin = 0;
    public filteredUsers: Observable<User[]> | null = null;
    public organization: Organization | null = null;

    private subscriptions: Subscription[] = [];
    constructor(
        public conf: ConfigProvider,
        public dialog: MatDialogRef<OrganizationsNewUserDialogComponent>,
        public utils: UtilsProvider,
        private _translater: TranslateService,
        private _toaster: MatSnackBar,
        private _users: UsersProvider,
        private _langs: LangsProvider,
        private _organizations: OrganizationsProvider,
        private _changeDetector: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) private _data: any
    ) {
        this.addAsAdmin = _data.addAsAdmin ? 1 : 0;
        this.organization = _data.organization;
        if (_data.avoidedUsers) {
            this.avoidedUsers = _data.avoidedUsers;
        }
        if (_data.enableSearch === false) {
            this.isSearchEnabled = false;
            this.isSearchMode = false;
        }
    }

    public ngAfterViewInit() {
        this.loadData()
            .then(() => {
                this.updateSearchMode(1);
                this.filteredUsers = this.emailFormControl.valueChanges.pipe(
                    map((user) => user && typeof user === 'object' ? user.email : user),
                    map((name) => name ? this.filter(name) : []), );
                this.subscriptions.push(
                    this.filteredUsers.subscribe((value) => { this.updateSearchMode(value.length); })
                );
                this.isLoading = false;
            })
            .catch((error) => this.onError(error));
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public onOptionSelected(event: any, user: User): void {
        this.isAdminError = false;
        this.alreadyMemberError = false;
        this.emailFormControl.updateValueAndValidity();

        this.firstNameFormControl.setValue(user.lastName);
        this.lastNameFormControl.setValue(user.firstName);
        this.firstNameFormControl.disable();
        this.lastNameFormControl.disable();
        this.selectedUser = user;
    }

    public onEmailKeyup(): void {
        this.isAdminError = false;
        this.alreadyMemberError = false;
        this.emailUnavailableError = false;
        this.emailFormControl.updateValueAndValidity();

        if (this.selectedUser) {
            this.firstNameFormControl.setValue('');
            this.lastNameFormControl.setValue('');
            this.firstNameFormControl.enable();
            this.lastNameFormControl.enable();
        }
        this.selectedUser = null;
    }

    public onAddClicked(): void {
        this.isLoading = true;
        this.add().subscribe({
          next: (result) => {
            this._toaster.open(this._translater.instant('User_added_to_organization'), '', { duration: 5000 });
            this.isLoading = false;
            this.dialog.close(result);
          },
          error: (error) => this.onError(error)
        });
    }

    public getSearchResult(user: User): string {
        return user.firstName + ' ' + user.lastName + ' (' + user.email + ')';
    }

    public filter(name: string): User[] {
        return this.availableUsers.filter((user: User, index: number, items: User[]) => this.getSearchResult(user).toLowerCase().includes(name.toLowerCase()));
    }

    public getUserEmail(user: User): string {
        return user.email ?? '';
    }

    private loadData(): Promise<any> {
        return new Promise((resolve, reject) => {
            // si la recherche est désactivée, on met aucun utilisateur disponible
            // ce qui aura pour conséquence de désactiver la recherche et de passer
            // en mode création
            if (!this.isSearchEnabled) {
                this.availableUsers = [];
                resolve(void 0);
            } else {
                // on récupère tous les utilisateurs dispo dans le cache
                // on y enlève tous ceux qui sont passés en paramètre "avoidedUsers"
                // et tous ceux qui sont admin
              // this.api.call('GET', 'users', {
              // notIn : this.utils.getKeyValues(this.avoidedUsers, 'id'),
              // isAdmin: 0
              // }, this._currentUser.getAccessToken())
                // this._storage.select(User)
                //     .where('id').notIn(this.utils.getKeyValues(this.avoidedUsers, 'id'))
                //     .and('isAdmin').equals(0)
                //     .orderBy('firstName')
                //     .get()
                //     .then((users) => {
                //         this.availableUsers = users;
                //         resolve();
                //     })
                //     .catch((error) => reject(error));
            }
        });
    }

    private checkEmail(value: any): any {
        if (value && !this.isSearchMode) {
            if (typeof value === 'object') {
                value = value.email;
            }

            if (new RegExp(this.utils.EMAIL_REGEX, 'g').test(value)) {
                return null;
            }

            return { invalidEmail: { valid: false } };
        }

        return null;
    }

    private checkName(value: string): any {
        if (!this.isSearchMode) {

            return value && value.length ? null : { name: { valid: false } };
        }

        return null;
    }

    private updateSearchMode(resultsCount: number): void {
        this.isSearchMode = this.isSearchEnabled && (resultsCount > 0 || !this.emailFormControl.value) && !this.selectedUser;
        if (this.isSearchMode) {
            this.searchPlaceholder = 'Search_email_or_name';
        } else {
            this.searchPlaceholder = 'Email_address';
            this.firstNameFormControl.updateValueAndValidity();
            this.lastNameFormControl.updateValueAndValidity();
        }
    }

    private add(): Observable<UserOrganizationRow> {
        return this.addUser().pipe(
          switchMap((user: User) => {
            return this.addMembership(user).pipe(
              map((membership: OrganizationMembership) => {
                return {
                  organization: this.organization,
                  user,
                  membership,
                  rolesIds: [],  // assuming getRolesIds is synchronous
                  roles: [] // hypothetical function to calculate roles
                } as UserOrganizationRow;
              })
            );
          })
        );
    }

    private addUser(): Observable<User> {
        if (this.selectedUser) {
          return of(this.selectedUser);
        }

        const user: User = new User({});
        user.id = 0;
        user.firstName = typeof this.firstNameFormControl.value === 'object' ?
            this.firstNameFormControl.value.firstName : this.firstNameFormControl.value;
        user.lastName = typeof this.lastNameFormControl.value === 'object' ?
            this.lastNameFormControl.value.lastName : this.lastNameFormControl.value;
        user.email = typeof this.emailFormControl.value === 'object' ?
            this.emailFormControl.value.email : this.emailFormControl.value;
        user.langId = this._langs.getBestLangId();

        user.isAdmin = this.addAsAdmin;
        return this._users.createUser(user).pipe(
            map((newUser: User) => {
                return newUser;
            }),
        );
    }

    private addMembership(user: User): Observable<OrganizationMembership> {
      const membership: OrganizationMembership = new OrganizationMembership({});
      membership.userId = user.id;
      membership.organizationId = this.organization!.id;
      return this._organizations.saveMembership(membership);
    }

    private onError(error: any): void {
        this.isLoading = false;

        if (error.status === 422) {
            let details: any = JSON.parse(error._body);
            if (details.errors) {
                details = details.errors;
            }

            if (details['isAdmin']) {
                this.isAdminError = true;
                this.emailFormControl.updateValueAndValidity();
                return;
            }
            if (details['projectMembershipStateId']) {
                this.alreadyMemberError = true;
                this.emailFormControl.updateValueAndValidity();
                return;
            }
            if (details['email']) {
                this.emailUnavailableError = true;
                this.emailFormControl.updateValueAndValidity();
                return;
            }
        }

        this._toaster.open(this._translater.instant('Error_unknown'), '', { duration: 5000 });
    }
}
